import React, { useState } from 'react';
import { BookOpen, Brain, Crown, HelpCircle, MessageCircle, MessageSquare, Settings, Sparkles, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface HelpPageProps {
  onNavigate?: (section: string) => void;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSection {
  title: string;
  icon: React.ReactNode;
  items: FAQItem[];
}

const HelpPage: React.FC<HelpPageProps> = ({ onNavigate }) => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleContactClick = () => {
    window.location.href = 'mailto:support@dostansenaprava.cz?subject=Dotaz k aplikaci';
  };

  const faqSections: FAQSection[] = [
    {
      title: 'Začínáme',
      icon: <Zap className="w-6 h-6" />,
      items: [
        {
          question: 'Jak začít studovat?',
          answer: 'Během registrace vás provede onboarding proces, po kterém se vám automaticky vytvoří personalizovaný studijní plán na míru. Ten najdete v sekci "Můj plán" a můžete ho kdykoliv upravit. Samozřejmě můžete také studovat svým vlastním tempem a procházet si jednotlivé sekce dle libosti.'
        },
        {
          question: 'Jak funguje sledování pokroku?',
          answer: 'Váš pokrok se automaticky ukládá při dokončování aktivit. Podrobný interaktivní přehled svého studia najdete v sekci Kapitoly pod tlačítkem "Studijní mapa", kde vidíte svůj postup vizuálně. Souhrnné statistiky najdete v nastavení profilu.'
        },
        {
          question: 'Jak vytvořit studijní plán?',
          answer: 'Po registraci vás provede onboarding, kde zodpovíte několik otázek o vašich cílech a dostupnosti. Na základě toho vám vytvoříme personalizovaný plán s doporučeným tempem studia, který najdete v sekci "Můj plán". Plán můžete kdykoliv změnit nebo upravit podle svých potřeb.'
        }
      ]
    },
    {
      title: 'Studijní funkce',
      icon: <BookOpen className="w-6 h-6" />,
      items: [
        {
          question: 'Co jsou flashkarty?',
          answer: 'Flashkarty jsou interaktivní kartičky s otázkami a odpověďmi, které vám pomohou procvičit si teorii. Klikněte na kartu pro zobrazení odpovědi.'
        },
        {
          question: 'Jak fungují kurzy?',
          answer: 'Kurzy obsahují strukturované lekce s textem, obrázky a kvízy. Postupujte podle kapitol a dokončujte kvízy pro ověření znalostí.'
        },
        {
          question: 'Co je audiokniha?',
          answer: 'Audiokniha vám umožní poslouchat teorii během cestování. Můžete si nastavit rychlost přehrávání podle svých potřeb.'
        }
      ]
    },
    {
      title: 'Premium',
      icon: <Crown className="w-6 h-6" />,
      items: [
        {
          question: 'Co zahrnuje Premium předplatné?',
          answer: 'S Premium získáte plný přístup ke všem ZSV modulům a neomezenému OSP Tutorovi. Můžete studovat bez omezení a využívat všechny funkce aplikace.'
        },
        {
          question: 'Jaké jsou možnosti předplatného?',
          answer: 'Nabízíme dvě varianty: Měsíční předplatné za 299 Kč (platí se každý měsíc) nebo 3měsíční předplatné za 199 Kč (platí se jednorázově na 3 měsíce dopředu). Druhá varianta je výhodnější pro dlouhodobé studium.'
        },
        {
          question: 'Je k dispozici zkušební období?',
          answer: 'Ano! Máte možnost vyzkoušet Premium na 3 dny zdarma. Během této doby můžete vyzkoušet všechny funkce bez jakéhokoliv závazku.'
        },
        {
          question: 'Jak mohu předplatné zrušit?',
          answer: 'Předplatné můžete kdykoliv zrušit v sekci Nastavení. Po zrušení vám zůstane přístup až do konce zaplaceného období. U 3měsíčního předplatného se peníze nevrací, ale můžete využít služby do konce období.'
        }
      ]
    },
    {
      title: 'OSP Tutor & AI',
      icon: <Brain className="w-6 h-6" />,
      items: [
        {
          question: 'Co je OSP Tutor?',
          answer: 'Váš osobní AI asistent dostupný 24/7. Pomůže vám s analýzou logických úloh, vysvětlí postupy řešení a odpoví na dotazy ohledně studia.'
        },
        {
          question: 'Co dělat, když AI přestane odpovídat?',
          answer: 'Pokud AI přestane odpovídat uprostřed konverzace nebo se zasekne, jednoduše obnovte stránku (F5 nebo Ctrl+R) a položte otázku znovu. Toto je občasný technický problém, který se vyřeší obnovením stránky.'
        },
        {
          question: 'Jaké jsou limity používání?',
          answer: 'Bezplatní uživatelé mají k dispozici 5 dotazů denně na AI tutora. Premium uživatelé získávají velký denní balíček promptů pro neomezené využití všech funkcí.'
        }
      ]
    },
    {
      title: 'Nastavení',
      icon: <Settings className="w-6 h-6" />,
      items: [
        {
          question: 'Jak změnit své údaje?',
          answer: 'Klikněte na svou profilovou fotku v pravém horním rohu a vyberte "Nastavení". Tam můžete upravit jméno, studijní cíle a další předvolby.'
        },
        {
          question: 'Jak přepnout mezi světlým a tmavým režimem?',
          answer: 'Klikněte na ikonu slunce/měsíce v pravém horním rohu pro přepnutí mezi režimy.'
        },
        {
          question: 'Jak spravovat předplatné?',
          answer: 'Předplatné můžete spravovat v sekci Nastavení. Zde ho můžete také kdykoliv zrušit.'
        }
      ]
    }
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-gradient-to-br from-[#ffaa00] to-[#ff8800] rounded-2xl border-b-[4px] border-[#cc8800]">
            <HelpCircle className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Nápověda a FAQ
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Všechno, co potřebujete vědět
            </p>
          </div>
        </div>
      </div>

      {/* FAQ Sections */}
      <div className="space-y-6">
        {faqSections.map((section, index) => (
          <div
            key={section.title}
            className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 border-b-[6px] dark:border-b-[6px] overflow-hidden"
          >
            <button
              onClick={() => setExpandedSection(expandedSection === section.title ? null : section.title)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#1cb0f6]/10 rounded-xl">
                  {section.icon}
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {section.title}
                </h2>
              </div>
              <div className={`transform transition-transform duration-200 ${expandedSection === section.title ? 'rotate-180' : ''}`}>
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            {expandedSection === section.title && (
              <div className="px-6 pb-4 space-y-4">
                {section.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="border-t border-gray-100 dark:border-gray-700 pt-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      {item.question}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Quick Tips Section */}
        <div className="bg-gradient-to-br from-[#ffaa00]/10 to-[#ff8800]/10 dark:from-[#ffaa00]/5 dark:to-[#ff8800]/5 rounded-2xl border-2 border-[#ffaa00]/30 border-b-[6px] p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-[#ffaa00] rounded-xl">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Rychlé tipy
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border-2 border-gray-100 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                Efektivní studium
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Studujte pravidelně každý den alespoň 20 minut. Je lepší krátké každodenní studium než dlouhé maratony jednou týdně.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border-2 border-gray-100 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                Zapamatování
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Používejte flashkarty pro aktivní vybavování. Mnohem efektivnější než jen pasivní čtení je zkoušení sebe sama.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border-2 border-gray-100 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                Multitasking
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Využijte audioknihu při cestování nebo sportu. Ušetříte čas a teorie vám bude lépe utkvívat v paměti.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border-2 border-gray-100 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                AI Pomoc
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Neváhejte použít OSP Tutora kdykoliv si nejste jisti. Umí vysvětlit logické úlohy krok za krokem.
              </p>
            </div>
          </div>
        </div>

        {/* Troubleshooting Section */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 border-b-[6px] p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            🔧 Řešení problémů
          </h2>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                AI Tutor přestal odpovídat
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Pokud AI přestane odpovídat uprostřed konverzace nebo se zasekne, jednoduše obnovte stránku (F5 nebo Ctrl+R) a položte otázku znovu. Toto je občasný technický problém.
              </p>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                Aplikace se načítá pomalu
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Zkuste obnovit stránku (Ctrl+F5) nebo vymazat cache prohlížeče. Ujistěte se, že máte stabilní internetové připojení.
              </p>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                Nefunguje mi audio
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Zkontrolujte, zda máte zapnutý zvuk v prohlížeči a na zařízení. Některé prohlížeče vyžadují interakci uživatele před přehráním audia.
              </p>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                Nevidím tlačítko na trial
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Zkuste obnovit stránku. Pokud jste trial již využili, nabídne se vám rovnou Premium verze.
              </p>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                Nedostal jsem email
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Zkontrolujte složku Spam/Hromadné. Chodí emaily o zahájení trialu, aktivaci Premium i zrušení.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="mt-8 bg-gradient-to-br from-[#1cb0f6]/10 to-[#1cb0f6]/5 dark:from-[#1cb0f6]/10 dark:to-[#1cb0f6]/5 rounded-2xl border-2 border-[#1cb0f6]/30 border-b-[6px] p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-[#1cb0f6] rounded-xl">
            <MessageCircle className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Kontaktujte nás
          </h2>
        </div>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Nenašli jste odpověď na svou otázku? Rádi vám pomůžeme! Odpovídáme obvykle do 24 hodin.
        </p>
        <div className="flex flex-wrap gap-4 mb-4">
          <button
            onClick={handleContactClick}
            className="inline-flex items-center px-6 py-3 bg-[#ffaa00] hover:bg-[#ff9900] text-white font-bold rounded-xl border-b-[4px] border-[#cc8800] active:border-b-0 active:translate-y-[4px] transition-all shadow-lg"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Napsat email
          </button>
          <button
            onClick={() => navigate('/feedback')}
            className="inline-flex items-center px-6 py-3 bg-[#4F46E5] hover:bg-[#4338CA] text-white font-bold rounded-xl border-b-[4px] border-[#3730A3] active:border-b-0 active:translate-y-[4px] transition-all shadow-lg"
          >
            <MessageSquare className="w-5 h-5 mr-2" />
            Zpětná vazba
          </button>
          <a
            href="https://instagram.com/dostansenaprava"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold rounded-xl border-b-[4px] border-purple-700 active:border-b-0 active:translate-y-[4px] transition-all shadow-lg"
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
            </svg>
            Instagram
          </a>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Nemáte emailového klienta? Napište nám na <span className="font-medium text-gray-700 dark:text-gray-300">support@dostansenaprava.cz</span>
        </p>
      </div>

      {/* Back to Home */}
      <div className="mt-8 text-center">
        <button
          onClick={() => onNavigate && onNavigate('home')}
          className="inline-flex items-center px-6 py-3 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-bold rounded-xl border-2 border-gray-200 dark:border-gray-700 border-b-[4px] active:border-b-2 active:translate-y-[2px] transition-all hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Zpět na hlavní stránku
        </button>
      </div>
    </div>
  );
};

export default HelpPage;